import { z } from 'zod'
import type { ContactVerification } from '../api/contact'
import type { DiditSummary } from '../api/kyc'
import { IsoDateTimeSchema } from '../primitives'
import { normalizeCountryIso3 } from './countries'
import {
  acceptedCitizenships,
  effectiveModules,
  MODULE_IDS,
  ModuleIdSchema,
  type ModuleId,
  type RequirementsV2,
} from './requirements'
import {
  decideResidence,
  residencePolicyHash,
  ResidenceEvidenceSchema,
  ResidenceDecisionSchema,
  ResidenceManualDecisionSchema,
  ResidenceVerdictSchema,
  type ResidenceDecision,
} from './residence'

/**
 * What a member PROVED (PLAN v4 §1.2 as amended by R7/R8): written by the decision pipeline (Didit decision, local
 * no-Didit decision, organizer approval) and by the v4 backfill, read LIVE by the eligibility check at CSP time
 * against the organization's CURRENT requirements. Raw evidence is stored (document nationality fields, residence
 * points), never a verdict: citizenship and residence are recomputed with the current regions / areas.
 */

/* ------------------------------------------------------------------ document / citizenship */

/** Didit `document_type` ("Identity Card", "Passport", "Residence Permit", …) → our code. */
export const NORMALIZED_DOCUMENT_TYPES = ['ID', 'P', 'RP', 'DL', 'OTHER'] as const
export type NormalizedDocumentType = (typeof NORMALIZED_DOCUMENT_TYPES)[number]

export function normalizeDocumentType(raw: string | null | undefined): NormalizedDocumentType | null {
  if (!raw) return null
  const s = raw.trim().toLowerCase().replace(/[_-]+/g, ' ')
  if (!s) return null
  if (s === 'id' || /\b(identity|id) card\b/.test(s) || s.includes('national id') || s === 'identity document')
    return 'ID'
  if (s === 'p' || s.includes('passport')) return 'P'
  if (s === 'rp' || s.includes('residence permit') || s.includes('resident card') || s.includes('residence card'))
    return 'RP'
  if (s === 'dl' || s.includes('driver') || s.includes('driving')) return 'DL'
  return 'OTHER'
}

/** Document fields kept as citizenship evidence (from the Didit OCR / MRZ). */
export const DocumentEvidenceSchema = z.object({
  /** Didit `document_type` verbatim. */
  type: z.string().nullable(),
  /** OCR holder nationality (ISO3, may be null when equal to the issuing state). */
  nationality: z.string().nullable(),
  /** Issuing country (ISO3). */
  issuingState: z.string().nullable(),
  /** MRZ nationality when present. */
  mrzNationality: z.string().nullable().default(null),
  /** Didit session the document was read in. */
  sessionId: z.string().nullable().default(null),
})
export type DocumentEvidence = z.infer<typeof DocumentEvidenceSchema>
export type DocumentEvidenceInput = z.input<typeof DocumentEvidenceSchema>

export interface CitizenshipResolution {
  /** ISO3 nationality, null when it cannot be told. */
  nationality: string | null
  source: 'nationality' | 'mrz' | 'issuing_state' | null
  documentType: NormalizedDocumentType | null
}

/**
 * THE citizenship resolver (R7), used by the KYC policy, the backfill and the live eligibility:
 * OCR nationality → MRZ nationality → issuing state, the latter ONLY for ID cards and passports (a residence permit
 * is issued by the country of residence, never the holder's citizenship). ISO2 / ISO3 accepted, normalized.
 */
export function resolveCitizenship(doc: DocumentEvidenceInput | null | undefined): CitizenshipResolution {
  const documentType = normalizeDocumentType(doc?.type)
  if (!doc) return { nationality: null, source: null, documentType }
  const nat = normalizeCountryIso3(doc.nationality)
  if (nat) return { nationality: nat, source: 'nationality', documentType }
  const mrz = normalizeCountryIso3(doc.mrzNationality)
  if (mrz) return { nationality: mrz, source: 'mrz', documentType }
  if (documentType === 'ID' || documentType === 'P') {
    const issuing = normalizeCountryIso3(doc.issuingState)
    if (issuing) return { nationality: issuing, source: 'issuing_state', documentType }
  }
  return { nationality: null, source: null, documentType }
}

export type CitizenshipVerdict = 'eligible' | 'not_eligible' | 'unknown'

/** Citizenship of a document vs a set of accepted ISO3 codes. */
export function citizenshipVerdict(
  doc: DocumentEvidenceInput | null | undefined,
  accepted: readonly string[]
): { verdict: CitizenshipVerdict; resolution: CitizenshipResolution } {
  const resolution = resolveCitizenship(doc)
  if (!resolution.nationality) return { verdict: 'unknown', resolution }
  return { verdict: accepted.includes(resolution.nationality) ? 'eligible' : 'not_eligible', resolution }
}

/**
 * A member's document vs the CURRENT accepted document types: `ok` when its normalized type is accepted,
 * `not_eligible` when it is another type (e.g. a passport after the organizer narrowed to ID cards), `unverified`
 * when the type cannot be told.
 */
export function documentTypeVerdict(
  doc: Pick<DocumentEvidenceInput, 'type'>,
  accepted: readonly string[]
): 'ok' | 'not_eligible' | 'unverified' {
  const t = normalizeDocumentType(doc.type)
  if (!t || t === 'OTHER') return 'unverified'
  return accepted.includes(t) ? 'ok' : 'not_eligible'
}

/* ------------------------------------------------------------------ assurance */

export const AGE_SOURCES = ['document', 'declared', 'declared_estimated'] as const
export const AgeSourceSchema = z.enum(AGE_SOURCES)
export type AgeSource = z.infer<typeof AgeSourceSchema>

export const ASSURANCE_ORIGINS = ['decision', 'organizer', 'backfill', 'test_fixture'] as const

export const MemberAssuranceSchema = z.object({
  version: z.literal(1).default(1),
  /** Email confirmed with a code (always required to join). */
  email: z.boolean(),
  /** Phone confirmed with a code. */
  phone: z.boolean(),
  /** Liveness passed (unique-human module). */
  uniqueHuman: z.boolean(),
  /** Identity document read and accepted (null: no document proof). */
  document: DocumentEvidenceSchema.nullable(),
  /** Where the date of birth used for age checks comes from (null: no DOB). */
  ageSource: AgeSourceSchema.nullable(),
  /** Didit liveness age estimate when present (live app; R16 cross-check). */
  estimatedAge: z.number().nullable().default(null),
  /** Residence evidence (raw) + the organizer's manual decision bound to the policy hash (R7). */
  residence: z
    .object({
      evidence: z.array(ResidenceEvidenceSchema).max(20),
      manual: ResidenceManualDecisionSchema.nullable().default(null),
      /**
       * The residence decision as computed with the EXACT evidence when the member's data was minimized after the final
       * decision (services/privacy.ts): the stored evidence is then coarse (~1 km), so while the residence policy is
       * unchanged (same policy hash) this snapshot decides instead of re-evaluating the coarse points.
       */
      decided: z
        .object({ policyHash: z.string().min(16), at: IsoDateTimeSchema, decision: ResidenceDecisionSchema })
        .nullable()
        .optional(),
    })
    .nullable()
    .default(null),
  /** Proofs come from a sandbox (test) verification: synthetic data, labeled in the UI (R8). */
  synthetic: z.boolean().default(false),
  /** Who wrote this assurance. */
  origin: z.enum(ASSURANCE_ORIGINS).default('decision'),
  /** requirementsHash() of the rules the member was verified against (null: unknown, e.g. backfill). */
  requirementsHash: z.string().nullable().default(null),
  updatedAt: IsoDateTimeSchema,
})
export type MemberAssurance = z.infer<typeof MemberAssuranceSchema>
export type MemberAssuranceInput = z.input<typeof MemberAssuranceSchema>

/**
 * Organizer waivers (R6): modules a member is NOT re-checked for, written when a settings change made existing
 * members non-compliant and the organizer chose "keep existing members eligible" (or by the v4 backfill for active
 * members, `by: 'migration'`). Merged on every change; never removed automatically.
 */
export const MemberExemptionsSchema = z.object({
  modules: z.array(ModuleIdSchema),
  at: IsoDateTimeSchema,
  /** Organizer id, or `migration`. */
  by: z.string(),
  note: z.string().max(2000).nullable().default(null),
})
export type MemberExemptions = z.infer<typeof MemberExemptionsSchema>

/** Merge new exempted modules into a member's exemptions (keeps the first `at` / `by` of earlier grants). */
export function mergeExemptions(
  current: MemberExemptions | null | undefined,
  add: readonly ModuleId[],
  meta: { at: string; by: string; note?: string | null }
): MemberExemptions | null {
  const modules = MODULE_IDS.filter((m) => current?.modules.includes(m) || add.includes(m))
  if (!modules.length) return current ?? null
  if (current && modules.length === current.modules.length) return current
  return { modules, at: meta.at, by: meta.by, note: meta.note ?? current?.note ?? null }
}

/**
 * Assurance from the evidence of a decided verification (Didit decision, organizer approval, backfill R8).
 * `approved`: the session outcome was approve or the organizer approved the member: only then do the liveness and
 * the document count as proofs (never from the member status alone).
 */
export function buildAssurance(input: {
  contact: ContactVerification | null | undefined
  summary: DiditSummary | null | undefined
  approved: boolean
  declaredDob: string | null | undefined
  origin: MemberAssurance['origin']
  requirementsHash?: string | null
  /** Residence evidence collected so far (P1: GPS / IP / document / PoA points). */
  residenceEvidence?: z.input<typeof ResidenceEvidenceSchema>[] | null
  previous?: MemberAssurance | null
  now: Date
}): MemberAssurance {
  const s = input.summary ?? null
  const synthetic = Boolean(s?.sandboxSynthetic)
  const livenessDeclined = s?.liveness?.status === 'Declined'
  const livenessSeen = Boolean(s?.liveness) || synthetic || Boolean(s?.features?.includes('LIVENESS'))
  const uniqueHuman = input.approved && livenessSeen && !livenessDeclined
  const ex = s?.extracted ?? null
  const document =
    input.approved && ex && (ex.documentType || ex.nationality || ex.issuingState || ex.documentNumber || synthetic)
      ? DocumentEvidenceSchema.parse({
          type: ex.documentType,
          nationality: ex.nationality,
          issuingState: ex.issuingState,
          mrzNationality: ex.mrzNationality ?? null,
          sessionId: s?.sessionId ?? null,
        })
      : null
  const estimatedAge = s?.liveness?.ageEstimation ?? input.previous?.estimatedAge ?? null
  const ageSource: MemberAssurance['ageSource'] =
    document && ex?.dateOfBirth && !synthetic
      ? 'document'
      : input.previous?.ageSource === 'document'
        ? 'document'
        : input.declaredDob
          ? estimatedAge !== null
            ? 'declared_estimated'
            : 'declared'
          : null
  const residence = input.residenceEvidence?.length
    ? {
        evidence: input.residenceEvidence.map((e) => ResidenceEvidenceSchema.parse(e)),
        manual: input.previous?.residence?.manual ?? null,
      }
    : (input.previous?.residence ?? null)
  return MemberAssuranceSchema.parse({
    version: 1,
    email: Boolean(input.contact?.email) || Boolean(input.previous?.email),
    phone: Boolean(input.contact?.phone) || Boolean(input.previous?.phone),
    uniqueHuman: uniqueHuman || Boolean(input.previous?.uniqueHuman),
    document: document ?? input.previous?.document ?? null,
    ageSource,
    estimatedAge,
    residence,
    synthetic: synthetic || Boolean(input.previous?.synthetic),
    origin: input.origin,
    requirementsHash: input.requirementsHash ?? null,
    updatedAt: input.now.toISOString(),
  })
}

/* ------------------------------------------------------------------ compliance */

/**
 * Per-module state of a member against requirements:
 * - not_required: the module is off;
 * - ok: proven (and, for citizenship / residence, eligible under the CURRENT lists / areas);
 * - exempt: waived by the organizer (R6);
 * - missing_proof: the member never provided this proof (module added after they joined, or no assurance at all);
 * - not_eligible: proven but outside the accepted set (other citizenship, residence outside the areas);
 * - unverified: proof exists but cannot decide (unreadable nationality, residence near / unknown / no evidence).
 */
export const MODULE_STATES = ['not_required', 'ok', 'exempt', 'missing_proof', 'not_eligible', 'unverified'] as const
export const ModuleStateSchema = z.enum(MODULE_STATES)
export type ModuleState = z.infer<typeof ModuleStateSchema>

export interface ModuleCompliance {
  modules: Record<ModuleId, ModuleState>
  /** Live residence decision (null when the module is off, exempt, or there is no residence assurance). */
  residence: ResidenceDecision | null
  /** Resolved document nationality (null when unknown / no document). */
  nationality: string | null
}

const NON_COMPLIANT: readonly ModuleState[] = ['missing_proof', 'not_eligible', 'unverified']
export const isNonCompliant = (s: ModuleState) => NON_COMPLIANT.includes(s)

/**
 * The ONE compliance computation (live eligibility, rule-change preview, backfill exemptions). A null assurance
 * proves nothing (fail closed): every enabled module is `missing_proof`.
 */
export function moduleCompliance(
  requirements: RequirementsV2,
  assurance: MemberAssurance | null | undefined,
  exemptions?: MemberExemptions | null
): ModuleCompliance {
  const m = effectiveModules(requirements)
  const a = assurance ?? null
  const exempt = new Set(exemptions?.modules ?? [])
  const state = (id: ModuleId, required: boolean, compute: () => ModuleState): ModuleState =>
    !required ? 'not_required' : exempt.has(id) ? 'exempt' : compute()

  let nationality: string | null = null
  let residence: ResidenceDecision | null = null
  const modules: Record<ModuleId, ModuleState> = {
    phone: state('phone', m.phone, () => (a?.phone ? 'ok' : 'missing_proof')),
    uniqueHuman: state('uniqueHuman', m.uniqueHuman, () => (a?.uniqueHuman ? 'ok' : 'missing_proof')),
    identityDocument: state('identityDocument', m.identityDocument, () => {
      if (!a?.document) return 'missing_proof'
      return documentTypeVerdict(a.document, requirements.identityDocument.documentTypes)
    }),
    citizenship: state('citizenship', m.citizenship, () => {
      if (!a?.document) return 'missing_proof'
      const { verdict, resolution } = citizenshipVerdict(a.document, acceptedCitizenships(requirements))
      nationality = resolution.nationality
      return verdict === 'eligible' ? 'ok' : verdict === 'not_eligible' ? 'not_eligible' : 'unverified'
    }),
    residence: state('residence', m.residence, () => {
      if (!a?.residence) return 'missing_proof'
      const config = requirements.residence
      const manual = a.residence.manual
      const policyHash = residencePolicyHash(config)
      if (manual && manual.policyHash === policyHash) return manual.result === 'inside' ? 'ok' : 'not_eligible'
      const decided = a.residence.decided
      residence =
        decided && decided.policyHash === policyHash ? decided.decision : decideResidence(a.residence.evidence, config)
      if (residence.outcome === 'approve') return 'ok'
      return residence.result === 'outside' ? 'not_eligible' : 'unverified'
    }),
  }
  if (nationality === null && a?.document) nationality = citizenshipVerdict(a.document, []).resolution.nationality
  return { modules, residence, nationality }
}

/** Modules a member does not comply with (exempt / not required / ok excluded). */
export const nonCompliantModules = (c: ModuleCompliance): ModuleId[] =>
  MODULE_IDS.filter((id) => isNonCompliant(c.modules[id]))

/* ------------------------------------------------------------------ badges (members table, R17) */

/** Compact view of a member's assurance vs the CURRENT requirements (members table / review queue badges). */
export const AssuranceBadgesSchema = z.object({
  email: z.boolean(),
  phone: z.boolean(),
  uniqueHuman: z.boolean(),
  document: z.boolean(),
  /** Resolved document nationality (ISO3). */
  nationality: z.string().nullable(),
  /** Live compliance per module. */
  modules: z.record(ModuleIdSchema, ModuleStateSchema),
  /** Live residence verdict (null when residence is off / no evidence / manual decision). */
  residence: ResidenceVerdictSchema.nullable(),
  /** The residence state comes from the organizer's manual decision (current policy hash). */
  residenceManual: z.boolean(),
  /** Proofs from a sandbox (test) verification. */
  synthetic: z.boolean(),
  /** No assurance recorded at all (nothing proven). */
  missing: z.boolean(),
})
export type AssuranceBadges = z.infer<typeof AssuranceBadgesSchema>

export function assuranceBadges(
  requirements: RequirementsV2,
  assurance: MemberAssurance | null | undefined,
  exemptions?: MemberExemptions | null
): AssuranceBadges {
  const c = moduleCompliance(requirements, assurance, exemptions)
  const manual =
    !!assurance?.residence?.manual &&
    c.modules.residence !== 'exempt' &&
    c.modules.residence !== 'not_required' &&
    assurance.residence.manual.policyHash === residencePolicyHash(requirements.residence)
  return {
    email: Boolean(assurance?.email),
    phone: Boolean(assurance?.phone),
    uniqueHuman: Boolean(assurance?.uniqueHuman),
    document: Boolean(assurance?.document),
    nationality: c.nationality,
    modules: c.modules,
    residence: c.residence?.result ?? null,
    residenceManual: manual,
    synthetic: Boolean(assurance?.synthetic),
    missing: !assurance,
  }
}
